// src/services/bookingService.js

// Helper to get API base URL based on environment
const getApiBaseUrl = () => {
    // Check if we're on localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return '/api';
    }
    // On production (cPanel)
    return 'https://nlngraclub.com/nlnapi';
};

const API_BASE_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('nlngra_token');

// Helper function to fetch with timeout
const fetchWithTimeout = (url, options, timeout = 30000) => {
    return Promise.race([
        fetch(url, options),
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout after ' + timeout + 'ms')), timeout)
        )
    ]);
};

// Helper function to handle API responses with better error messages
const handleResponse = async(response, endpointName = 'API') => {
    console.log(`🔧 ${endpointName} - Response status:`, response.status);
    console.log(`🔧 ${endpointName} - Response URL:`, response.url);

    // Handle successful but empty responses
    if (response.status === 204 || (response.status === 200 && response.headers.get('content-length') === '0')) {
        return { success: true, message: 'Request successful' };
    }

    // Get raw response text
    const text = await response.text();
    console.log(`📡 ${endpointName} - Raw response preview:`, text.substring(0, 500));
    console.log(`📡 ${endpointName} - Raw response length:`, text.length);

    // Check if response is empty
    if (!text || text.trim() === '') {
        console.error(`❌ ${endpointName} - Empty response received`);
        return { success: false, message: 'Empty response from server' };
    }

    // Clean the response from PHP warnings/notices
    let cleanResponse = text;

    // Remove PHP warnings and notices
    cleanResponse = cleanResponse.replace(/<br\s*\/?>\s*<b>Warning<\/b>.*?(?=<br|<\\?|\{)/gis, '');
    cleanResponse = cleanResponse.replace(/<b>Warning<\/b>.*?<br\s*\/?>/gis, '');
    cleanResponse = cleanResponse.replace(/<b>Notice<\/b>.*?<br\s*\/?>/gis, '');
    cleanResponse = cleanResponse.replace(/<br\s*\/?>\s*<b>.*?<\/b>.*?\n/g, '');
    cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');
    cleanResponse = cleanResponse.replace(/<[^>]*>/g, '');
    cleanResponse = cleanResponse.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    // Remove any non-JSON prefixes
    cleanResponse = cleanResponse.replace(/^[^{]*(\{)/, '$1');

    // Try to find JSON in the cleaned response
    const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
        const jsonStr = jsonMatch[0];
        console.log(`🔍 ${endpointName} - Found JSON:`, jsonStr.substring(0, 200));
        try {
            const parsed = JSON.parse(jsonStr);
            console.log(`✅ ${endpointName} - JSON parsed successfully`);

            // Add success message if not present
            if (parsed.success === undefined) {
                parsed.success = true;
            }
            if (parsed.message === undefined && parsed.success) {
                parsed.message = 'Request completed successfully';
            }

            return parsed;
        } catch (e) {
            console.error(`❌ ${endpointName} - JSON parse error:`, e);
            return {
                success: false,
                message: `Invalid JSON response: ${e.message}`,
                rawResponse: jsonStr.substring(0, 200)
            };
        }
    }

    // If no JSON found, return error
    console.error(`❌ ${endpointName} - No JSON found in response`);
    return {
        success: false,
        message: 'Invalid server response format',
        rawResponse: cleanResponse.substring(0, 200)
    };
};

export const bookingService = {
    // ============================================
    // BOOKING ENDPOINTS
    // ============================================

    // Create a new booking
    createBooking: async(bookingData) => {
        try {
            console.log('📡 Creating booking with data:', bookingData);
            console.log('📡 API_BASE_URL:', API_BASE_URL);

            const url = `${API_BASE_URL}/bookings.php?action=create`;
            console.log('📡 Full URL:', url);
            console.log('📡 Token exists:', !!getToken());

            const response = await fetchWithTimeout(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(bookingData)
            }, 30000);

            const result = await handleResponse(response, 'Create Booking');

            if (result.success) {
                console.log('✅ Booking created successfully:', result);
                return result;
            } else {
                console.error('❌ Booking creation failed:', result.message);
                return result;
            }
        } catch (error) {
            console.error('❌ Create booking error:', error);
            return {
                success: false,
                message: error.message || 'Network error. Please check your connection.'
            };
        }
    },

    // Get user's bookings
    getUserBookings: async(filters = {}) => {
        try {
            const params = new URLSearchParams({
                action: 'list',
                _t: Date.now(),
                ...filters
            }).toString();

            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?${params}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            }, 15000);

            const result = await handleResponse(response, 'Get User Bookings');

            if (result.success) {
                console.log(`✅ Loaded ${result.bookings?.length || 0} bookings`);
                return result;
            } else {
                console.error('❌ Failed to load bookings:', result.message);
                return { success: false, bookings: [], message: result.message };
            }
        } catch (error) {
            console.error('Get user bookings error:', error);
            return { success: false, bookings: [], message: error.message };
        }
    },

    // Get single booking
    getBooking: async(bookingId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?action=get&id=${bookingId}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            }, 15000);
            return await handleResponse(response, 'Get Booking');
        } catch (error) {
            console.error('Get booking error:', error);
            return { success: false, message: error.message };
        }
    },

    // Cancel booking
    cancelBooking: async(bookingId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?action=cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ booking_id: bookingId })
            }, 15000);
            return await handleResponse(response, 'Cancel Booking');
        } catch (error) {
            console.error('Cancel booking error:', error);
            return { success: false, message: error.message };
        }
    },

    // Get booking stats
    getStats: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?action=stats&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            }, 15000);
            return await handleResponse(response, 'Get Stats');
        } catch (error) {
            console.error('Get stats error:', error);
            return { success: false, message: error.message };
        }
    },

    // Admin: Get all bookings
    adminGetAllBookings: async(filters = {}) => {
        try {
            const params = new URLSearchParams({
                action: 'admin_list',
                _t: Date.now(),
                ...filters
            }).toString();

            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?${params}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            }, 20000);
            return await handleResponse(response, 'Admin Get Bookings');
        } catch (error) {
            console.error('Admin get all bookings error:', error);
            return { success: false, bookings: [], message: error.message };
        }
    },

    // Check availability
    checkAvailability: async({ facility_id, booking_date, start_time, end_time }) => {
        try {
            const params = new URLSearchParams({
                action: 'check_availability',
                facility_id,
                booking_date,
                start_time,
                end_time,
                _t: Date.now()
            }).toString();

            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?${params}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Check Availability');
        } catch (error) {
            console.error('Check availability error:', error);
            return { success: false, available: false, message: error.message };
        }
    },

    // Admin: Update booking status
    adminUpdateBooking: async(bookingId, status, reason = null, newLocation = null) => {
        try {
            const body = {
                booking_id: bookingId,
                status: status
            };
            if (reason) body.reason = reason;
            if (newLocation) body.new_location = newLocation;

            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?action=admin_update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(body)
            }, 15000);
            return await handleResponse(response, 'Admin Update Booking');
        } catch (error) {
            console.error('Admin update booking error:', error);
            return { success: false, message: error.message };
        }
    },

    // Get items for a specific booking
    getBookingItems: async(bookingId) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?action=booking_items&booking_id=${bookingId}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            }, 15000);
            return await handleResponse(response, 'Get Booking Items');
        } catch (error) {
            console.error('Get booking items error:', error);
            return { success: false, items: [], message: error.message };
        }
    },

    // Return items (member)
    returnItems: async(bookingId, itemIds, conditionNotes = '') => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?action=return_items`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    booking_id: bookingId,
                    item_ids: itemIds,
                    condition_notes: conditionNotes
                })
            }, 15000);
            return await handleResponse(response, 'Return Items');
        } catch (error) {
            console.error('Return items error:', error);
            return { success: false, message: error.message };
        }
    },

    // Issue items to member (admin only)
    issueItems: async(bookingId, items) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?action=issue_items`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    booking_id: bookingId,
                    items: items
                })
            }, 15000);
            return await handleResponse(response, 'Issue Items');
        } catch (error) {
            console.error('Issue items error:', error);
            return { success: false, message: error.message };
        }
    },

    // Get pending reconciliations (admin only)
    getPendingReconciliations: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?action=pending_reconciliations&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            }, 15000);
            return await handleResponse(response, 'Get Pending Reconciliations');
        } catch (error) {
            console.error('Get pending reconciliations error:', error);
            return { success: false, bookings: [], message: error.message };
        }
    },

    // Complete reconciliation (admin only)
    completeReconciliation: async(bookingId, notes = '') => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/bookings.php?action=complete_reconciliation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    booking_id: bookingId,
                    notes: notes
                })
            }, 15000);
            return await handleResponse(response, 'Complete Reconciliation');
        } catch (error) {
            console.error('Complete reconciliation error:', error);
            return { success: false, message: error.message };
        }
    },

    // ============================================
    // FACILITIES ENDPOINTS (PUBLIC - NO AUTH)
    // ============================================

    // Get all facilities
    getFacilities: async(category = null, maintenance = 'operational') => {
        try {
            let url = `${API_BASE_URL}/facilities.php?action=list&maintenance=${maintenance}&_t=${Date.now()}`;
            if (category) {
                url += `&category=${encodeURIComponent(category)}`;
            }
            console.log('📡 Fetching facilities from:', url);

            // Facilities endpoint - NO AUTH NEEDED
            const response = await fetchWithTimeout(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 15000);

            const result = await handleResponse(response, 'Get Facilities');

            if (result.success) {
                console.log(`✅ Loaded ${result.facilities?.length || 0} facilities`);
                return result;
            } else {
                console.error('❌ Failed to load facilities:', result.message);
                return { success: false, facilities: [], message: result.message };
            }
        } catch (error) {
            console.error('Get facilities error:', error);
            return { success: false, facilities: [], message: error.message };
        }
    },

    // Get facility by ID
    getFacilityById: async(id) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/facilities.php?action=get&id=${id}&_t=${Date.now()}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Facility');
        } catch (error) {
            console.error('Get facility by ID error:', error);
            return { success: false, message: error.message };
        }
    },

    // Get facility categories
    getFacilityCategories: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/facilities.php?action=categories&_t=${Date.now()}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Facility Categories');
        } catch (error) {
            console.error('Get facility categories error:', error);
            return { success: false, categories: [], message: error.message };
        }
    },

    // Search facilities
    searchFacilities: async(query) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/facilities.php?action=search&q=${encodeURIComponent(query)}&_t=${Date.now()}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Search Facilities');
        } catch (error) {
            console.error('Search facilities error:', error);
            return { success: false, facilities: [], message: error.message };
        }
    },

    // ============================================
    // EQUIPMENT ENDPOINTS (PUBLIC - NO AUTH)
    // ============================================

    // Get all equipment - PUBLIC endpoint (NO AUTH)
    getEquipment: async(sportingOnly = null, category = null) => {
        try {
            let url = `${API_BASE_URL}/equipment.php?action=list&_t=${Date.now()}`;
            if (sportingOnly !== null) {
                url += `&sporting_only=${sportingOnly}`;
            }
            if (category) {
                url += `&category=${encodeURIComponent(category)}`;
            }

            console.log('📡 Fetching equipment from PUBLIC endpoint:', url);

            // IMPORTANT: NO Authorization header for public equipment endpoint!
            const response = await fetchWithTimeout(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 15000);

            const result = await handleResponse(response, 'Get Equipment');

            if (result.success) {
                console.log(`✅ Loaded ${result.equipment?.length || 0} equipment items`);
                return result;
            } else {
                console.error('❌ Failed to load equipment:', result.message);
                return { success: false, equipment: [], message: result.message };
            }
        } catch (error) {
            console.error('Get equipment error:', error);
            return { success: false, equipment: [], message: error.message, error: error.message };
        }
    },

    // Get equipment categories - PUBLIC
    getEquipmentCategories: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/equipment.php?action=categories&_t=${Date.now()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Equipment Categories');
        } catch (error) {
            console.error('Get equipment categories error:', error);
            return { success: false, categories: [], message: error.message };
        }
    },

    // Get equipment by category - PUBLIC
    getEquipmentByCategory: async(category) => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/equipment.php?action=by_category&category=${encodeURIComponent(category)}&_t=${Date.now()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Equipment By Category');
        } catch (error) {
            console.error('Get equipment by category error:', error);
            return { success: false, equipment: [], message: error.message };
        }
    },

    // ============================================
    // ROLES & PERMISSIONS ENDPOINTS (AUTH REQUIRED)
    // ============================================

    // Get user role by email
    getUserRole: async(email) => {
        try {
            console.log('📡 Fetching user role from:', `${API_BASE_URL}/roles.php`);
            const response = await fetchWithTimeout(`${API_BASE_URL}/roles.php?action=user_role&email=${encodeURIComponent(email)}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get User Role');
        } catch (error) {
            console.error('Get user role error:', error);
            return { success: false, message: error.message };
        }
    },

    // Get current user's permissions
    getMyPermissions: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/roles.php?action=my_permissions&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Permissions');
        } catch (error) {
            console.error('Get permissions error:', error);
            return { success: false, message: error.message };
        }
    },

    // Get available users for delegation
    getAvailableUsers: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/roles.php?action=available_users&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Available Users');
        } catch (error) {
            console.error('Get available users error:', error);
            return { success: false, users: [], message: error.message };
        }
    },

    // ============================================
    // LOOKUP DATA ENDPOINTS (PUBLIC)
    // ============================================

    // Get all lookup data
    getLookupData: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/lookup.php?action=all&_t=${Date.now()}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Lookup Data');
        } catch (error) {
            console.error('Get lookup data error:', error);
            return { success: false, data: {}, message: error.message };
        }
    },

    // Get jersey sizes
    getJerseySizes: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/lookup.php?action=jersey_sizes&_t=${Date.now()}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Jersey Sizes');
        } catch (error) {
            console.error('Get jersey sizes error:', error);
            return { success: false, sizes: [], message: error.message };
        }
    },

    // Get time slots
    getTimeSlots: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/lookup.php?action=time_slots&_t=${Date.now()}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Time Slots');
        } catch (error) {
            console.error('Get time slots error:', error);
            return { success: false, slots: [], message: error.message };
        }
    },

    // Get event types
    getEventTypes: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/lookup.php?action=event_types&_t=${Date.now()}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Event Types');
        } catch (error) {
            console.error('Get event types error:', error);
            return { success: false, event_types: [], message: error.message };
        }
    },

    // Get booking statuses
    getBookingStatuses: async() => {
        try {
            const response = await fetchWithTimeout(`${API_BASE_URL}/lookup.php?action=booking_statuses&_t=${Date.now()}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            }, 10000);
            return await handleResponse(response, 'Get Booking Statuses');
        } catch (error) {
            console.error('Get booking statuses error:', error);
            return { success: false, booking_statuses: [], message: error.message };
        }
    }
};